import {
    useCallback,
    useMemo,
    useState,
    type MouseEvent,
    type ReactNode
} from 'react';
import { FormattedMessage } from 'react-intl';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Badge from '@mui/material/Badge';
import Divider from '@mui/material/Divider';
import Tooltip from '@mui/material/Tooltip';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import AddToolButton from './AddToolButton';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import FilterListIcon from '@mui/icons-material/FilterList';
import DownloadIcon from '@mui/icons-material/Download';
import {
    Toolbar,
    ToolbarButton,
    ColumnsPanelTrigger,
    FilterPanelTrigger
} from '../lib/dataGrid';
import GridQuickFilter from './GridQuickFilter';
import GridSettingsMenu from './GridSettingsMenu';
import { useGridPreferences } from './useGridPreferences';
import { useGridSessionState } from './useGridSessionState';
import ActiveToolButton from './ActiveToolButton';
import GridCsvExportDialog from './GridCsvExportDialog';
import { GridCsvExportContext, germanText } from './gridCsvExport';

// Declaration-merged, so this augmentation is shared by every grid.
declare module '@mui/x-data-grid-pro' {
    interface ToolbarPropsOverrides {
        /** Add-button handler; omit (or canAdd:false) to hide the add button. */
        onAdd?: () => void;
        canAdd?: boolean;
        /** i18n id for the add button's tooltip (e.g. 'diary.add'). */
        addMessageId?: string;
        /**
         * Turns the add button into a small MENU, for when what the record will
         * BE decides which fields the form asks for (the Befunde grid:
         * Befundbeschreibung vs. baulicher Bestand). Replaces `onAdd`.
         */
        addMenu?: AddMenuEntry[];
        /**
         * Show the "full" toolbar tools — Columns + Filters panel buttons and
         * the quick-filter box (the touch/field grid stays bare).
         */
        showQuickFilter?: boolean;
        /** Extra controls rendered before the search box (e.g. a group filter). */
        filterControls?: ReactNode;
        /**
         * Remembers this grid's column layout and sorting per user+device. Must
         * be UNIQUE per grid — an office grid and its Auswertung twin need
         * separate ids (see useGridPreferences).
         */
        gridId?: string;
        /**
         * Columns this grid ships hidden — a layout saved before they existed
         * must not switch them on (see readPrefs).
         */
        hiddenColumns?: string[];
        /**
         * Appended to the CSV's file name when the page alone does not say
         * what the grid holds: a record's name (a Nachweis), or which of
         * several grids on one page. `exportNameId` is an i18n id, written in
         * German whatever the UI language.
         */
        exportName?: string;
        exportNameId?: string;
    }
}

export interface AddMenuEntry {
    messageId: string;
    onSelect: () => void;
}

/**
 * The standard grid toolbar: add button on the LEFT, everything else pushed
 * RIGHT for the office grids (`showQuickFilter`). Too narrow to fit them all (a
 * phone), the toolbar scrolls horizontally and the search box collapses to an
 * icon. Wire it with `slots={{ toolbar: GridCrudToolbar }}` and pass the props
 * via `slotProps.toolbar`.
 */
export default function GridCrudToolbar({
    onAdd,
    canAdd = false,
    addMessageId = '',
    addMenu,
    showQuickFilter = false,
    filterControls,
    gridId,
    hiddenColumns,
    exportName,
    exportNameId
}: {
    onAdd?: () => void;
    canAdd?: boolean;
    addMessageId?: string;
    addMenu?: AddMenuEntry[];
    showQuickFilter?: boolean;
    filterControls?: ReactNode;
    gridId?: string;
    hiddenColumns?: string[];
    exportName?: string;
    exportNameId?: string;
}) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    // Only from `md` up is there room for the add button to say what it makes;
    // narrower, the label would push the tools beside it off the edge.
    const showAddLabel = useMediaQuery(theme.breakpoints.up('md'));
    // Both no-ops without a `gridId` (the field's touch grids).
    useGridPreferences(gridId, hiddenColumns);
    // For this session only: the filter and scroll offset the list was left at
    // (see gridSessionState).
    useGridSessionState(gridId);

    // The search box follows the breakpoint until the user expands/collapses it
    // themselves. Deriving it (rather than seeding state from `useMediaQuery`)
    // keeps the two in sync while the media query settles on the first render.
    const [userExpandedSearch, setUserExpandedSearch] = useState<
        boolean | null
    >(null);
    const searchExpanded = userExpandedSearch ?? !isMobile;
    // Expanded on a phone, the search field claims the free space instead of its
    // fixed width — so the spacer must not take that space first.
    const searchFills = searchExpanded && isMobile;
    const [addAnchor, setAddAnchor] = useState<null | HTMLElement>(null);
    const addClick = (event: MouseEvent<HTMLElement>) => {
        if (addMenu) {
            setAddAnchor(event.currentTarget);
            return;
        }
        onAdd?.();
    };
    /**
     * MUI folds an EMPTY search box away as soon as it loses focus (and on
     * Escape). On a phone that is the right trade; on a desktop the box IS the
     * toolbar's resting state, so only a phone may close it.
     */
    const changeSearchExpanded = (expanded: boolean) => {
        if (isMobile || expanded) {
            setUserExpandedSearch(expanded);
        }
    };

    // The CSV export: an ExportMenu among the `filterControls` takes it in as
    // an entry; without one the toolbar shows its own button.
    const [csvDialogOpen, setCsvDialogOpen] = useState(false);
    const [exportMenuCount, setExportMenuCount] = useState(0);
    const registerExportMenu = useCallback(() => {
        setExportMenuCount((count) => count + 1);
        return () => setExportMenuCount((count) => count - 1);
    }, []);
    const csvExport = useMemo(
        () => ({
            open: () => setCsvDialogOpen(true),
            register: registerExportMenu
        }),
        [registerExportMenu]
    );

    return (
        <Toolbar
            /**
             * The grid's own Toolbar right-aligns its content, which would
             * spill the tools that no longer fit off the LEFT edge, where the
             * grid clips them. Start-align instead; the spacer below still
             * pushes the office tools right whenever there IS room. `overflowY`
             * must be pinned wherever we scroll: CSS promotes a `visible` axis
             * to `auto` next to a scrolling one, and the 1px bottom border then
             * shows a vertical scrollbar. Toolbar takes no `sx`; it forwards
             * plain div props.
             */
            style={{
                justifyContent: 'flex-start',
                ...(showQuickFilter
                    ? { overflowX: 'auto', overflowY: 'hidden' }
                    : {})
            }}
        >
            {canAdd &&
                (onAdd || addMenu) &&
                (showAddLabel ? (
                    <Button
                        variant="text"
                        startIcon={<AddIcon />}
                        onClick={addClick}
                        sx={{ mr: 1, flexShrink: 0 }}
                    >
                        <FormattedMessage id={addMessageId} />
                    </Button>
                ) : (
                    <Tooltip title={<FormattedMessage id={addMessageId} />}>
                        <AddToolButton onClick={addClick}>
                            <AddIcon fontSize="small" />
                        </AddToolButton>
                    </Tooltip>
                ))}
            {addMenu && (
                <Menu
                    anchorEl={addAnchor}
                    open={Boolean(addAnchor)}
                    onClose={() => setAddAnchor(null)}
                >
                    {addMenu.map((entry) => (
                        <MenuItem
                            key={entry.messageId}
                            onClick={() => {
                                setAddAnchor(null);
                                entry.onSelect();
                            }}
                        >
                            <FormattedMessage id={entry.messageId} />
                        </MenuItem>
                    ))}
                </Menu>
            )}
            {showQuickFilter && (
                <GridCsvExportContext.Provider value={csvExport}>
                    {/* Push the tools to the right; add button stays on the left. */}
                    {!searchFills && <Box sx={{ flex: 1 }} />}
                    {filterControls}
                    {exportMenuCount === 0 && (
                        <Tooltip
                            title={
                                <FormattedMessage id="grid.csvExport.menu" />
                            }
                        >
                            <ToolbarButton
                                onClick={() => setCsvDialogOpen(true)}
                            >
                                <DownloadIcon fontSize="small" />
                            </ToolbarButton>
                        </Tooltip>
                    )}
                    <Tooltip title={<FormattedMessage id="grid.columns" />}>
                        <ColumnsPanelTrigger render={<ToolbarButton />}>
                            <ViewColumnIcon fontSize="small" />
                        </ColumnsPanelTrigger>
                    </Tooltip>
                    <Tooltip title={<FormattedMessage id="grid.filters" />}>
                        <FilterPanelTrigger
                            render={(triggerProps, state) => (
                                <ActiveToolButton
                                    {...triggerProps}
                                    // A filter hides rows, so the button says
                                    // so — filled, and with HOW MANY.
                                    active={state.filterCount > 0}
                                >
                                    <Badge
                                        badgeContent={state.filterCount}
                                        color="info"
                                    >
                                        <FilterListIcon fontSize="small" />
                                    </Badge>
                                </ActiveToolButton>
                            )}
                        />
                    </Tooltip>
                    <GridSettingsMenu gridId={gridId} />
                    <Divider
                        orientation="vertical"
                        flexItem
                        sx={{ mx: 0.5, my: 0.5 }}
                    />
                    <GridQuickFilter
                        expanded={searchExpanded}
                        onExpandedChange={changeSearchExpanded}
                        fills={searchFills}
                    />
                    {csvDialogOpen && (
                        <GridCsvExportDialog
                            exportName={
                                exportName ??
                                (exportNameId
                                    ? germanText(exportNameId)
                                    : undefined)
                            }
                            gridId={gridId}
                            onClose={() => setCsvDialogOpen(false)}
                        />
                    )}
                </GridCsvExportContext.Provider>
            )}
        </Toolbar>
    );
}
